import {
  FC, memo, useEffect, useState,
} from 'react';
import { Text, Title, Table } from 'shared/ui';
import { OrderForm } from 'pages/Orders/components/OrderForm';
import { OrderRequestDto } from 'shared/api';
import { getOrders, Order, orderSelectors } from 'entities/order';
import { useOrders } from 'pages/Orders/hooks/useOrders';
import { useEngines } from 'pages/Engines/hooks/useEngines';
import { useAppDispatch, useAppSelector } from 'app/providers/Store';
import { orderActions } from 'entities/order/model/slice/order.slice';
import { ENGINE_ORDERS_TABLE_COLUMNS } from '../../../constants/engineConstants';
import styles from './EngineOrdersSection.module.scss';

interface EngineOrdersSectionProps {
  engineId: number | null;
}

export const EngineOrdersSection: FC<EngineOrdersSectionProps> = memo(({
  engineId,
}) => {
  const { createNewOrder, updateExistingOrder } = useOrders();
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [isOrderFormOpen, setIsOrderFormOpen] = useState(false);
  const dispatch = useAppDispatch();
  const { loadEngineDetail } = useEngines();
  const orders = useAppSelector(orderSelectors.getOrders);

  useEffect(() => {
    if (engineId) {
      dispatch(getOrders({ engineId }));
    }
    return () => {
      dispatch(orderActions.dropDetailData());
    };
  }, [engineId]);

  const handleRequestMaintenance = () => {
    setEditingOrder(null);
    setIsOrderFormOpen(true);
  };

  const handleOrderFormClose = () => {
    setIsOrderFormOpen(false);
    setEditingOrder(null);
  };

  const handleOrderSubmit = (data: OrderRequestDto) => {
    if (editingOrder?.id) {
      updateExistingOrder({
        id: editingOrder.id!,
        data: {
          ...data,
          engineId,
        },
        onSuccess: () => {
          loadEngineDetail(engineId);
        },
      });
    } else {
      createNewOrder({
        data: {
          ...data,
          status: OrderRequestDto.status.NEW,
          engineId,
        },
        onSuccess: () => {
          loadEngineDetail(engineId);
        },
      });
    }

    setIsOrderFormOpen(false);
    setEditingOrder(null);
  };

  const actions = [
    {
      key: 'edit',
      label: 'Редактировать',
      onClick: (record: Order) => {
        setEditingOrder(record);
        setIsOrderFormOpen(true);
      },
    },
    {
      key: 'cancel',
      label: 'Отменить',
      onClick: (record: Order) => {
        updateExistingOrder({
          id: record.id!,
          data: {
            ...record,
            engineId: engineId!,
            status: OrderRequestDto.status.CANCELLED,
          },
          onSuccess: () => {
            loadEngineDetail(engineId!);
          },
        });
      },
      danger: true,
    },
  ];

  return (
    <div className={styles.ordersSection}>
      <div className={styles.ordersHeader}>
        <Title level={5}>Запросы на обслуживание</Title>
        <button
          type="button"
          className={styles.requestButton}
          onClick={handleRequestMaintenance}
        >
          Запрос на ТО
        </button>
      </div>

      {orders && orders.length > 0 ? (
        <Table
          columns={ENGINE_ORDERS_TABLE_COLUMNS}
          dataSource={orders}
          hasPagination={false}
          actions={actions}
        />
      ) : (
        <Text>Запросы на обслуживание отсутствуют</Text>
      )}

      {isOrderFormOpen && (
        <OrderForm
          editingOrder={editingOrder}
          isEditing={!!editingOrder}
          onSubmit={handleOrderSubmit}
          onCancel={handleOrderFormClose}
        />
      )}
    </div>
  );
});

EngineOrdersSection.displayName = 'EngineOrdersSection';
